import React from "react";
import PropTypes from "prop-types";
import { formatCurrency } from "shared/@utils/money";
import CircleBadge from "../CircleBadge";
import styles from "./ProductItemCard.module.css";

export function ProductItemCard({
  product,
  children,
  actionColWidth,
  onTagClick,
  activeTags,
  imageBadge,
  selected,
  onToggleSelect,
  selectable,
  showEmptyTagDivider,
  onOpen,
}) {
  const [expandedTags, setExpandedTags] = React.useState(false);
  const {
    image,
    name,
    date,
    retailPrice,
    pm,
    variationLabel,
    variationValue,
    size,
    sku,
    productType,
    status,
    statusLabel,
    pinLevels = [],
    memberships = [],
    tags = [],
    author,
    unitsSold,
  } = product;

  // Archived reads as red so a product that is not live is obvious at a
  // glance; every other status stays in the default text colour.
  const statusKey = String(status || statusLabel || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  const statusIsInactive = statusKey === "archived";

  const maxVisibleTags = 9;
  const visibleTags = expandedTags ? tags : tags.slice(0, maxVisibleTags);
  const hasOverflow = tags.length > maxVisibleTags;

  return (
    <article
      className={`${styles.item}${
        selectable ? " " + styles.itemSelectable : ""
      }`}
    >
      <div
        className={`${styles.infoRow}${
          selectable ? " " + styles.infoRowSelectable : ""
        }`}
        style={{ "--action-col-width": actionColWidth }}
      >
        {selectable && (
          <div className={styles.checkCell}>
            <input
              type="checkbox"
              checked={selected || false}
              onChange={onToggleSelect}
              aria-label={`Select ${name}`}
            />
          </div>
        )}

        <div className={styles.imageCell}>
          {onOpen ? (
            <button
              type="button"
              className={styles.openThumb}
              onClick={() => onOpen(product)}
              aria-label={`Open ${name}`}
              title={`Open ${name}.`}
            >
              {image ? (
                <img src={image} alt={name} />
              ) : (
                <div className={styles.imagePlaceholder}>No image</div>
              )}
            </button>
          ) : image ? (
            <img src={image} alt={name} title={name} />
          ) : (
            <div className={styles.imagePlaceholder}>No image</div>
          )}
          {imageBadge && <div className={styles.imageBadge}>{imageBadge}</div>}
        </div>

        <div className={styles.detailsCell}>
          <h4 className={styles.name} title={name}>
            {onOpen ? (
              <button
                type="button"
                className={styles.openName}
                onClick={() => onOpen(product)}
                title={`Open ${name}.`}
              >
                {name}
              </button>
            ) : (
              name
            )}
          </h4>
          {date && <p className={styles.date}>{date}</p>}
          <div className={styles.prices}>
            {retailPrice !== undefined && retailPrice !== null && (
              <span>
                Retail:{" "}
                <span className={styles.priceValue}>
                  {formatCurrency(retailPrice)}
                </span>
              </span>
            )}
            {pm !== undefined && pm !== null && (
              <span>
                <span title="Premier Member">PM</span>:{" "}
                <span className={styles.priceValue}>{formatCurrency(pm)}</span>
              </span>
            )}
          </div>

          {variationValue && (
            <p className={styles.metaLine}>
              <span
                className={`${styles.metaLabel} ${styles.metaLabelHighlight}`}
              >
                {variationLabel || "Variation"}:
              </span>{" "}
              <span className={styles.metaValue}>{variationValue}</span>
            </p>
          )}

          {author && (
            <p className={styles.metaLine}>
              <span
                className={`${styles.metaLabel} ${styles.metaLabelHighlight}`}
              >
                Author:
              </span>{" "}
              <span className={styles.metaValue}>{author}</span>
            </p>
          )}

          {typeof unitsSold === "number" && (
            <p className={styles.metaLine}>
              <span className={styles.metaLabel}>Sold:</span>{" "}
              <span className={styles.metaValue}>{unitsSold}</span>
            </p>
          )}

          {size && (
            <p className={styles.metaLine}>
              <span className={styles.metaLabel}>Size:</span>{" "}
              <span className={styles.metaValue}>{size}</span>
            </p>
          )}
        </div>

        <div className={styles.skuCell} title={sku || undefined}>
          {sku}
        </div>
        <div
          className={styles.typeCell}
          title={typeof productType === "string" ? productType : undefined}
        >
          {productType}
        </div>
        <div className={styles.statusCell}>
          <span
            className={`${styles.statusLabel}${
              statusIsInactive ? " " + styles.statusInactive : ""
            }`}
          >
            {statusLabel}
          </span>
          <div className={styles.badgeStack}>
            <div className={styles.pinLevels}>
              {pinLevels.map((level) => (
                <span
                  className={styles.pinLevel}
                  key={`${name}-${level.label}`}
                >
                  <CircleBadge
                    label={level.label}
                    title={level.title}
                    bgColor={level.bgColor || "#ffffff"}
                    textColor={level.textColor || "#000000"}
                    size="medium"
                  />
                </span>
              ))}
            </div>
            {!!memberships.length && (
              <div className={styles.membershipLevels}>
                {memberships.map((level) => (
                  <span
                    className={styles.pinLevel}
                    key={`${name}-mem-${level.label}`}
                  >
                    <CircleBadge
                      label={level.label}
                      title={level.title}
                      bgColor={level.bgColor || "#ffffff"}
                      textColor={level.textColor || "#000000"}
                      size="medium"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.actionCell}>{children}</div>
      </div>

      {!!visibleTags.length && (
        <div
          className={`${styles.tagsRow} ${
            expandedTags ? styles.tagsExpanded : ""
          }`}
        >
          {visibleTags.map((tag, index) => {
            const isActive = activeTags.includes(tag);
            return (
              <button
                type="button"
                className={`${styles.tag} ${isActive ? styles.tagActive : ""}`}
                key={`${name}-tag-${index}`}
                onClick={() => onTagClick(tag)}
                aria-pressed={isActive}
                title={
                  isActive ? `Remove "${tag}" filter` : `Filter by "${tag}"`
                }
              >
                {tag}
              </button>
            );
          })}
          {hasOverflow && (
            <button
              type="button"
              className={styles.tagEllipsis}
              onClick={() => setExpandedTags(!expandedTags)}
              aria-label={expandedTags ? "Collapse tags" : "Expand tags"}
              title={expandedTags ? "Show fewer tags" : "Show all tags"}
            >
              {expandedTags ? "−" : "•••"}
            </button>
          )}
        </div>
      )}
      {!visibleTags.length && showEmptyTagDivider && (
        <div className={styles.divider} />
      )}
    </article>
  );
}

ProductItemCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.string,
    retailPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variationLabel: PropTypes.string,
    variationValue: PropTypes.string,
    size: PropTypes.string,
    sku: PropTypes.string,
    productType: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    status: PropTypes.string,
    statusLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    pinLevels: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bgColor: PropTypes.string,
        textColor: PropTypes.string,
      })
    ),
    memberships: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bgColor: PropTypes.string,
        textColor: PropTypes.string,
      })
    ),
    tags: PropTypes.arrayOf(PropTypes.string),
    author: PropTypes.string,
    // Units sold; only present when the list is sorted by popularity.
    unitsSold: PropTypes.number,
  }).isRequired,
  children: PropTypes.node,
  actionColWidth: PropTypes.string,
  onTagClick: PropTypes.func,
  activeTags: PropTypes.arrayOf(PropTypes.string),
  imageBadge: PropTypes.node,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  selectable: PropTypes.bool,
  showEmptyTagDivider: PropTypes.bool,
  /* When supplied, the thumbnail and the title become a second way to reach
     whatever the row's primary action does — useful once the action column has
     scrolled out of reach on a narrow list. */
  onOpen: PropTypes.func,
};

ProductItemCard.defaultProps = {
  children: null,
  actionColWidth: "220px",
  onTagClick: () => {},
  activeTags: [],
  imageBadge: null,
  selected: false,
  onToggleSelect: undefined,
  selectable: false,
  showEmptyTagDivider: true,
  onOpen: undefined,
};

export default React.memo(ProductItemCard);
